import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

export interface CreatePopupProps {
  /** Popup shell style — centered modal or a slide-over panel from the right edge. */
  variant: 'modal' | 'sidebar'
  title: string
  onClose: () => void
  children: React.ReactNode
  /** Optional decorative icon shown in a circular badge next to the title. */
  icon?: React.ReactNode
  /** Custom width override (relative units — vw/%/min(), never px). Omit for the compact default. */
  width?: string
  /**
   * Maximum popup height (relative units — vh/%/min(), never px). Omit for the compact
   * default. This is always a CAP, never a fixed height — the panel shrinks to fit its
   * content and only grows up to this value, so a short form doesn't get stretched into
   * a mostly-empty box.
   */
  height?: string
  /**
   * Whether to render the built-in title bar (icon + title + close button) and the
   * body's own padding. Defaults to `false` because `children` is almost always a
   * form that already renders its own complete header/actions (e.g.
   * `MasterPatternViewCreateForm`, whose form has a title, Cancelar/Guardar and its
   * own spacing) — the popup has no way to know whether `children` self-paints a
   * header, so it assumes it does. Set to `true` only when `children` is genuinely
   * headerless and should rely on the popup's own title bar instead. Escape-to-close
   * still works either way; the host's own Cancelar just needs to call the same
   * `onClose`.
   * @default false
   */
  showHeader?: boolean
}

/**
 * Popup chrome for LookupField's inline "create new" affordance.
 * Owns the overlay, header (title + close button), and Escape-to-close behavior.
 * The host supplies the form content via `children` — this component knows nothing
 * about what's being created.
 */
export function CreatePopup({ variant, title, onClose, children, icon, width, height, showHeader = false }: CreatePopupProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Lock the host page's own scroll while this popup is open — otherwise the page
  // behind the dimmed overlay still scrolls under it, which does nothing useful (the
  // popup itself is fixed) and just reads as broken. Restores whatever value was
  // there before (not a hardcoded 'visible'), so nested popups (e.g. a lookup's own
  // create popup opened from inside another lookup's create popup) don't have the
  // inner one's cleanup accidentally unlock scroll the outer one still needs locked.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  const bodyRef = useRef<HTMLDivElement>(null)
  // Sticky "tallest content seen" height. Without this, a tabbed form (e.g. a
  // MasterPatternViewCreateForm master) makes the whole popup visibly grow/shrink on
  // every tab click, since the panel shrinks-to-fit whichever tab happens to be
  // active — reads as broken, not responsive. Once a taller tab has been shown, the
  // popup stops shrinking back down for a shorter one; a form with no real height
  // variance across its own content (no tabs, or same-sized tabs) just keeps
  // settling at its own natural height, same as before.
  const [stickyHeight, setStickyHeight] = useState<number>()
  // Re-measures `scrollHeight` (the content's real extent regardless of any clipping)
  // on every DOM mutation inside body, via MutationObserver rather than a `children`
  // dependency: tab-switching lives entirely in `MasterPatternViewForm`'s OWN internal
  // state, so the `children` element CreatePopup receives never changes reference when
  // the user clicks a different tab — only the DOM it renders does (content swapped,
  // or a `hidden` class/attribute toggled). A plain effect keyed on `children` would
  // only ever measure once, at mount. `characterData`/`attributes` are included
  // because a tab switch can change nothing but a `hidden` attribute/class on already-
  // mounted panels, with no nodes added or removed.
  useLayoutEffect(() => {
    const el = bodyRef.current
    if (!el) return
    const measure = () => {
      setStickyHeight((prev) => (prev === undefined ? el.scrollHeight : Math.max(prev, el.scrollHeight)))
    }
    measure()
    const observer = new MutationObserver(measure)
    observer.observe(el, { childList: true, subtree: true, attributes: true, characterData: true })
    return () => observer.disconnect()
  }, [])

  const isSidebar = variant === 'sidebar'

  return createPortal(
    <div
      className="lookup-field-create__overlay"
      style={{ justifyContent: isSidebar ? 'flex-end' : 'center' }}
    >
      <div
        className={`lookup-field-create__panel${showHeader ? '' : ' lookup-field-create__panel--flush'}`}
        data-variant={variant}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        // Set as custom properties (consumed by the width/max-width/max-height rules
        // in the stylesheet) rather than direct inline `maxWidth`/`maxHeight` — an
        // inline style always wins over ANY stylesheet rule regardless of specificity
        // or media queries, which would permanently defeat the narrow-viewport
        // breakpoint below for every consumer that passes its own createWidth/
        // createHeight (i.e. almost everyone, since MasterPatternView's own default
        // already sets one). Routing through a custom property lets the responsive
        // rule override the EFFECTIVE width/height on narrow screens while still
        // letting the per-instance value win at normal sizes.
        style={{
          ...(width ? { '--lf-create-width': width } : {}),
          ...(height ? { '--lf-create-height': height } : {}),
          ...(stickyHeight ? { minHeight: `${stickyHeight}px` } : {}),
        } as React.CSSProperties}
      >
        {showHeader && (
          <div className="lookup-field-create__header">
            <div className="lookup-field-create__heading">
              {icon && <span className="lookup-field-create__icon">{icon}</span>}
              <h3 className="lookup-field-create__title">{title}</h3>
            </div>
            <button
              type="button"
              className="lookup-field-create__close"
              onClick={onClose}
              aria-label="Cerrar"
            >
              <X size={16} />
            </button>
          </div>
        )}
        <div
          ref={bodyRef}
          className={showHeader ? 'lookup-field-create__body' : 'lookup-field-create__body lookup-field-create__body--flush'}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  )
}
